import axios, { AxiosError, AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import { SOMETHING_WENT_WRONG } from "../constants";
import { reportError } from "../utils/errors";
import useAuth from "../hooks/auth/useAuth";
import TokenService from "../utils/token";
import { useToastAlert } from "../components/app/ToastAlert/ToastAlertProvider";
import { Network } from "@capacitor/network";
import { App } from "@capacitor/app";
import { isCapacitorApp } from "../utils/mobile";
import ToastAlertNetworkErrorContent from "../components/app/ToastAlert/ToastAlertNetworkErrorContent";
import { WifiOffIcon } from "lucide-react";
import { UpdateToastOptions } from "../utils/toast";

export interface AxiosInstanceProps {
  children: React.ReactNode;
}
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const SIGN_IN_URL = "api/users/signin";
const REFRESH_URL = "api/users/refresh";

const SERVER_HEALTH_CHECK_URL = "/health";
const HEALTH_CHECK_DELAY = 60000; // 1 minute

const NETWORK_ALERT_ID = "network-toast-alert";

const compareUrls = (url?: string, urlToCompare?: string) => {
  return url?.toLowerCase()?.endsWith(urlToCompare?.toLowerCase());
};

const AxiosInterceptor = ({ children }: AxiosInstanceProps) => {
  const [isSet, setIsSet] = useState(false);
  const [isUserOnline, setIsUserOnline] = useState(navigator.onLine);
  const [isServerOnline, setIsServerOnline] = useState(true);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [isAppVisible, setIsAppVisible] = useState(true);

  const { logout } = useAuth();
  const { showToastAlert, updateToastAlert, closeToastAlert } = useToastAlert();

  const showNetworkAlert: typeof showToastAlert = (severity, options) => {
    return showToastAlert(severity, {
      toastId: NETWORK_ALERT_ID,
      ...options,
    });
  };

  const updateNetworkAlert = (options: UpdateToastOptions) => {
    return updateToastAlert(NETWORK_ALERT_ID, {
      ...options,
      toastId: NETWORK_ALERT_ID,
    });
  };

  const closeNetworkAlert = () => {
    closeToastAlert(NETWORK_ALERT_ID);
  };

  const healthCheckEnabled =
    process.env.REACT_APP_SERVER_HEALTH_CHECK_ENABLED === "true";

  const networkAlert = (
    <ToastAlertNetworkErrorContent
      userOffline={!isUserOnline}
      serverOffline={!isServerOnline}
    />
  );

  const checkServerAvailability = async () => {
    try {
      await axiosInstance.get(SERVER_HEALTH_CHECK_URL);
      setIsServerOnline(true);
    } catch (error) {
      setIsServerOnline(false);
    }
  };

  // Check server availability every minute
  useEffect(() => {
    if (!healthCheckEnabled || !isAppVisible) return;
    const interval = setInterval(async () => {
      if (isUserOnline) {
        await checkServerAvailability();
      }
    }, HEALTH_CHECK_DELAY);

    return () => clearInterval(interval);
  }, [isUserOnline]);

  // Check server availability when app becomes visible
  useEffect(() => {
    if (!healthCheckEnabled || !isAppVisible || !isUserOnline) return;
    checkServerAvailability();
  }, [isAppVisible, isUserOnline]);

  // Setup web app network status listener
  useEffect(() => {
    if (isCapacitorApp) return;

    const updateOnlineStatus = () => setIsUserOnline(navigator.onLine);
    const updateAppVisible = () => setIsAppVisible(!document.hidden);

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);
    document.addEventListener("visibilitychange", updateAppVisible);

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
      document.removeEventListener("visibilitychange", updateAppVisible);
    };
  }, []);

  // Setup mobile app network status listener using Capacitor
  useEffect(() => {
    if (!isCapacitorApp) return;

    // Get initial network status
    const getInitialNetworkStatus = async () => {
      try {
        const status = await Network.getStatus();
        setIsUserOnline(status.connected);
      } catch (error) {
        console.error("Error getting initial network status", error);
      }
    };

    getInitialNetworkStatus();

    const capacitorHandler = Network.addListener(
      "networkStatusChange",
      (status) => {
        setIsUserOnline(status.connected);
      },
    );

    const appStateHandler = App.addListener("appStateChange", async (state) => {
      setIsAppVisible(state.isActive);
    });

    return () => {
      capacitorHandler && capacitorHandler.then((handler) => handler.remove());
      appStateHandler && appStateHandler.then((handler) => handler.remove());
    };
  }, []);

  useEffect(() => {
    if (!isAppVisible) return;

    const isOffline = !isUserOnline || !isServerOnline;

    const networkAlertOptions = {
      autoClose: false as const,
      message: networkAlert,
      theme: "colored",
      closeButton: false,
      draggable: false,
      icon: <WifiOffIcon />,
    };

    if (isOffline) {
      if (alertVisible) {
        updateNetworkAlert({
          severity: "error",
          ...networkAlertOptions,
        });
      } else {
        showNetworkAlert("error", {
          ...networkAlertOptions,
        });
        setAlertVisible(true);
      }
    } else if (alertVisible) {
      closeNetworkAlert();
      setAlertVisible(false);
    }
  }, [isUserOnline, isServerOnline]);

  useEffect(() => {
    const responseOnFulfilledInterceptor = (response: AxiosResponse) => {
      setIsServerOnline(true);
      return response;
    };

    const responseOnRejectedInterceptor = async (err: AxiosError) => {
      let sendErrorReport = true;
      const requestUrl = err?.config?.url;

      if (!navigator.onLine || !isServerOnline) {
        !isServerOnline && reportError(err);
        return Promise.reject(err);
      }

      if (
        err?.response?.status === 401 &&
        !compareUrls(requestUrl, SIGN_IN_URL)
      ) {
        showToastAlert("error", {
          message: "Your session has timed out, please login once again.",
        });
        sendErrorReport = false;
        logout();
      } else if (!err.response && err.code === AxiosError.ERR_NETWORK) {
        setIsServerOnline(false);
      } else if (!err.response) {
        showToastAlert("error", {
          message: SOMETHING_WENT_WRONG,
        });
      }
      sendErrorReport && reportError(err);
      return Promise.reject(err);
    };

    const requestOnFulfilledInterceptor = async (config: any) => {
      if (
        compareUrls(config.url, SIGN_IN_URL) ||
        compareUrls(config.url, REFRESH_URL) ||
        compareUrls(config.url, SERVER_HEALTH_CHECK_URL)
      )
        return config;

      try {
        const token = await TokenService.getAccessToken();

        if (token) {
          config.headers = {
            ...(config.headers || {}),
            Authorization: `Bearer ${token}`,
          };
        }
      } catch (e) {
        console.error("Error in getting token", e);
      }

      return config;
    };

    const requestOnRejectedInterceptor = (err: AxiosError) => {
      return Promise.reject(err);
    };

    const responseInterceptor = axiosInstance.interceptors.response.use(
      responseOnFulfilledInterceptor,
      responseOnRejectedInterceptor,
    );

    const requestInterceptor = axiosInstance.interceptors.request.use(
      requestOnFulfilledInterceptor,
      requestOnRejectedInterceptor,
    );

    setIsSet(true);
    return () => {
      axiosInstance.interceptors.response.eject(responseInterceptor);
      axiosInstance.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  return <>{isSet && children}</>;
};

export { axiosInstance, REFRESH_URL, SIGN_IN_URL };
export default AxiosInterceptor;
